import { cn } from "@gettactic/helpers";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

const dialogContentVariants = cva(
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-90 data-[state=open]:zoom-in-90 fixed top-[50%] left-[50%] z-[250] grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 overflow-auto duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in",
  {
    variants: {
      variant: {
        default:
          "bg-white border shadow-lg max-w-2xl p-6 sm:rounded-brand md:w-full",
        kiosk: "bg-white max-w-lg rounded-3xl p-10 shadow-2xl",
        kioskDark:
          "bg-slate-800/80 backdrop-blur-xl max-w-md rounded-2xl p-8 shadow-2xl"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

const dialogCloseButtonVariants = cva(
  "absolute rounded-sm ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "top-6 right-6 opacity-70 data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
        kiosk:
          "top-7.5 right-7 opacity-60 hover:bg-neutral-100 rounded-full p-2",
        kioskDark:
          "top-4 right-4 text-white/60 hover:bg-white/10 hover:text-white rounded-full p-2"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

const dialogCloseIconVariants = cva("", {
  variants: {
    variant: {
      default: "h-4 w-4",
      kiosk: "h-6 w-6",
      kioskDark: "h-6 w-6"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});

const dialogOverlayVariants = cva(
  // Shared backdrop for every modal: a rounded, tinted scrim. Change the color
  // or radius here to update all modals at once.
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-drawer rounded-[16px] bg-[rgba(4,13,40,0.32)] data-[state=closed]:animate-out data-[state=open]:animate-in",
  {
    variants: {
      variant: {
        default: "",
        kiosk: "",
        kioskDark: ""
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = (props: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);

DialogPortal.displayName = DialogPrimitive.Portal.displayName;

interface DialogOverlayProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>,
    VariantProps<typeof dialogOverlayVariants> {}

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, variant, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(dialogOverlayVariants({ variant }), className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  /** Props of the backdrop. */
  overlayProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;
  /** Replaces the Close button in the corner. */
  customCloseButton?: React.ReactNode;
  /**
   * Scope the overlay and content to a positioned ancestor instead of the
   * viewport. When true, `fixed` positioning is swapped for `absolute`, so the
   * backdrop fills — and the content centers within — {@link portalContainer}.
   */
  contained?: boolean;
  /** DOM element to portal the dialog into. Used with {@link contained}. */
  portalContainer?: HTMLElement | null;
}

/** Lets a `DialogDescription` tell its dialog to be described by it. */
const DialogDescriptionIdContext = React.createContext<
  ((id: string | undefined) => void) | null
>(null);

/**
 * The dialog panel, centred over a tinted backdrop with a Close button; `variant` "kiosk"
 * and "kioskDark" are the kiosk app's dialogs. Name it with `DialogTitle`; a
 * `DialogDescription` inside describes it.
 */
const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      overlayProps,
      variant,
      customCloseButton,
      contained,
      portalContainer,
      ...props
    },
    ref
  ) => {
    const [descriptionId, setDescriptionId] = React.useState<string>();
    return (
      <DialogPortal container={portalContainer ?? undefined}>
        <DialogOverlay
          variant={variant}
          {...overlayProps}
          className={cn(contained && "absolute", overlayProps?.className)}
        >
          <DialogPrimitive.Content
            ref={ref}
            // Most dialogs have no DialogDescription, and Radix warns "Missing
            // Description or aria-describedby" unless this is set: it points at the
            // description when there is one and is explicitly empty otherwise
            aria-describedby={descriptionId}
            className={cn(
              dialogContentVariants({ variant }),
              contained && "absolute",
              className
            )}
            onCloseAutoFocus={() => {
              // This fixes the isse of body keeping the pointer events disabled after closing the dialog.
              // Focus still goes back to what opened the dialog.
              document.body.style.pointerEvents = "";
            }}
            {...props}
          >
            <DialogDescriptionIdContext.Provider value={setDescriptionId}>
              {children}
            </DialogDescriptionIdContext.Provider>
            {customCloseButton}
            {!customCloseButton && (
              <DialogPrimitive.Close
                className={dialogCloseButtonVariants({ variant })}
              >
                <X className={dialogCloseIconVariants({ variant })} />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </DialogOverlay>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

export type DialogFooterProps = React.HTMLAttributes<HTMLDivElement>;

const DialogFooter = ({ className, ...props }: DialogFooterProps) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "font-semibold text-lg leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, id, ...props }, ref) => {
  const generatedId = React.useId();
  const descriptionId = id ?? generatedId;
  const registerDescription = React.useContext(DialogDescriptionIdContext);
  React.useLayoutEffect(() => {
    registerDescription?.(descriptionId);
    return () => registerDescription?.(undefined);
  }, [registerDescription, descriptionId]);
  return (
    <DialogPrimitive.Description
      ref={ref}
      id={descriptionId}
      className={cn("text-content text-sm", className)}
      {...props}
    />
  );
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  dialogContentVariants,
  dialogCloseButtonVariants,
  dialogCloseIconVariants,
  dialogOverlayVariants
};
